import React from 'react';
import { ExclamationTriangleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

/**
 * Componente para mostrar advertencias de cuota cuando se está cerca del límite
 * @param {Object} props
 * @param {string} props.resourceType - Tipo de recurso (sucursales, clientes, vehiculos, mantenimientos)
 * @param {Object} props.usageMetric - Métrica de uso del recurso
 * @param {Function} props.onUpgrade - Callback cuando se hace clic en "Mejorar plan" (opcional)
 * @param {string} props.className - Clases CSS adicionales (opcional)
 */
const QuotaWarning = ({ 
  resourceType, 
  usageMetric, 
  onUpgrade, 
  className = '' 
}) => {
  // No mostrar si no hay métrica o no está cerca del límite
  if (!usageMetric || !usageMetric.isNearLimit) {
    return null;
  }

  // Mapeo de nombres de recursos para mostrar al usuario
  const resourceNames = {
    sucursales: {
      singular: 'sucursal',
      plural: 'sucursales'
    },
    clientes: {
      singular: 'cliente', 
      plural: 'clientes',
      icon: '👤'
    },
    vehiculos: {
      singular: 'vehículo',
      plural: 'vehículos',
      icon: '🚗'
    },
    mantenimientos: {
      singular: 'mantenimiento',
      plural: 'mantenimientos',
      icon: '🔧'
    },
    repuestos: {
      singular: 'repuesto',
      plural: 'repuestos',
      icon: '🔩'
    },
    servicios: {
      singular: 'servicio',
      plural: 'servicios',
      icon: '🛠️'
    },
    usuarios: {
      singular: 'usuario',
      plural: 'usuarios',
      icon: '👥'
    }
  };

  const resourceName = resourceNames[resourceType] || { 
    singular: resourceType, 
    plural: resourceType,
    icon: '📊'
  };

  // Determinar el color de la advertencia según qué tan cerca esté del límite
  const isVeryClose = usageMetric.remaining <= 5;
  const isCritical = usageMetric.remaining <= 2;
  const isFull = usageMetric.remaining <= 0;

  const getWarningStyles = () => {
    if (isCritical || isFull) {
      return {
        container: 'bg-red-500/10 border border-red-500',
        icon: 'text-red-500',
        text: 'text-gray-100',
        accent: 'text-white font-semibold',
        button: 'bg-red-500 hover:bg-red-600 text-white border-red-600'
      };
    } else if (isVeryClose) {
      return {
        container: 'bg-orange-500/10 border border-orange-500',
        icon: 'text-orange-500',
        text: 'text-gray-100',
        accent: 'text-orange-100',
        button: 'bg-orange-500 hover:bg-orange-600 text-white border-orange-600'
      };
    } else {
      return {
        container: 'bg-yellow-500/10 border border-yellow-500',
        icon: 'text-yellow-500',
        text: 'text-gray-100',
        accent: 'text-yellow-100',
        button: 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600'
      };
    }
  };

  const styles = getWarningStyles();
  
  // Mensaje de estado
  const statusMessage = isFull 
    ? `Límite alcanzado (${usageMetric.percentage}%)`
    : isCritical 
      ? `Crítico: ${usageMetric.remaining} ${usageMetric.remaining === 1 ? resourceName.singular : resourceName.plural} restantes`
      : isVeryClose
        ? `Cuidado: ${usageMetric.remaining} ${usageMetric.remaining === 1 ? resourceName.singular : resourceName.plural} restantes`
        : `Uso actual: ${usageMetric.percentage}%`;

  return (
    <div className={`rounded-lg p-3 mb-4 border-gray-600 ${styles.container} ${className}`}>
      <div className="flex items-center justify-between gap-3">
        {/* Icono y mensaje principal */}
        <div className="flex items-center min-w-0">
          <ExclamationTriangleIcon className={`h-5 w-5 ${styles.icon} flex-shrink-0`} />
          <div className="ml-3">
            <p className={`text-sm font-medium ${styles.text} ${styles.accent}`}>
              {usageMetric.actual} de {usageMetric.max} {resourceName.plural} permitidos
            </p>
            <p className="text-xs text-gray-300 mt-0.5">
              {statusMessage}
            </p>
          </div>
        </div>
        
        {/* Botón de acción */}
        {onUpgrade && (
          <button
            onClick={onUpgrade}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center ${styles.button} whitespace-nowrap border`}
          >
            <span>Mejorar plan</span>
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuotaWarning;